import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Mail } from 'lucide-react';

const SOCIALS = [
  { name: 'Linkedin', icon: Linkedin, href: 'https://www.linkedin.com/company/orbiax/services/request-proposal' },
  { name: 'Mail', icon: Mail, href: 'mailto:contacto@orbiax.co' },
];

const LEGAL_LINKS = ['Términos y Condiciones', 'Política y Privacidad de Datos', 'Acuerdo de Nivel de Servicio'];

// Corta el fondo en dos: una cuña clara arriba-izquierda y el resto oscuro,
// igual que la referencia. En mobile la caja es angosta y alta -- se usa una
// cuña chica (15% de ancho), bien lejos de la línea violeta del circuito,
// para que quepa sin invadir el texto, que en mobile también queda acotado
// al 85% de ancho (ml-auto) para no pisarla nunca. Desde md, la cuña original.
const DIAGONAL_CLIP_CLASS =
  "[clip-path:polygon(0%_18%,15%_0%,100%_0%,100%_100%,0%_100%)] md:[clip-path:polygon(0%_55%,45%_0%,100%_0%,100%_100%,0%_100%)]";

// Recibe el trazo que baja desde Beneficios (mismo x: 38%) y baja pegado a
// la diagonal hasta la esquina inferior izquierda, y de ahí cruza recto por
// abajo hasta la "O" del wordmark de fondo -- el circuito termina
// fundiéndose con el propio isotipo de la marca.
const FooterCircuit = () => (
  <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
      <path
        d="M 38 0 L 0 55 L 0 100 L 49 100 L 49 87"
        fill="none"
        stroke="#8B5CF6"
        strokeOpacity="0.4"
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
    <div className="absolute left-0 top-[55%] w-[11px] h-[11px] -translate-y-1/2 rounded-full bg-[#A78BFA] opacity-90" />
    <div className="absolute left-[49%] top-[87%] w-[11px] h-[11px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#A78BFA] opacity-90" />
  </div>
);

const MainframeFooter = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative z-[1] bg-white overflow-hidden">
      <div className="relative min-h-[560px] sm:min-h-[620px]">
        <div className={`absolute inset-0 bg-black ${DIAGONAL_CLIP_CLASS}`} />
        <FooterCircuit />

        {/* wordmark de fondo encuadrado en su propia "O" -- se recorta el
            resto del isotipo para que el círculo quede fijo en el punto
            donde el circuito termina (left: 46%) */}
        <div
          className="absolute overflow-hidden opacity-[0.07] pointer-events-none select-none w-[50%] md:w-[60%]"
          style={{ left: '46%', bottom: '8%', height: '34%' }}
        >
          <img
            src="/assets/logoblanco.png"
            alt=""
            aria-hidden="true"
            className="absolute left-0 top-0 h-full w-auto"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
        </div>

        <div className="relative z-10 h-full flex flex-col justify-between px-5 sm:px-8 md:px-10 pt-20 sm:pt-28 pb-6 sm:pb-8">
          <div className="max-w-[85%] ml-auto text-right md:max-w-md">
            <p
              style={{ fontSize: 'clamp(24px, 3.4vw, 36px)', lineHeight: 1.15, fontWeight: 500, letterSpacing: '-0.01em' }}
              className="text-white mb-4"
            >
              ¿Listo para llevar tu empresa a esta órbita?
            </p>
            <p className="text-white/60 text-[14px] sm:text-[15px] leading-relaxed mb-6">
              Hablemos de tu operación y diseñemos juntos el despegue: procesos optimizados,
              aplicaciones a la medida y agentes de IA trabajando por ti.
            </p>
            <div className="flex flex-wrap items-center justify-end gap-4">
              <a
                href="mailto:contacto@orbiax.co"
                className="inline-flex items-center gap-2 bg-white text-black text-[14px] font-medium px-6 py-3 rounded-full hover:opacity-80 transition-opacity"
              >
                Escríbenos
                <span aria-hidden="true">→</span>
              </a>
              <Link
                to="/diagnostico-gratuito"
                className="inline-flex items-center gap-1 text-white text-[14px] font-medium border border-white/30 px-6 py-3 rounded-full hover:bg-white hover:text-black transition-colors duration-200"
              >
                Agendar diagnóstico
                <span aria-hidden="true">›</span>
              </Link>
            </div>
          </div>

          {/* debajo del logo de fondo (que ocupa hasta el 8% desde abajo) */}
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6 ml-auto max-w-md w-full">
            <div className="flex items-center gap-2">
              {SOCIALS.map(({ name, icon: SocialIcon, href }) => (
                <a
                  key={name}
                  href={href}
                  target={href.startsWith('http') ? '_blank' : undefined}
                  rel={href.startsWith('http') ? 'noreferrer' : undefined}
                  className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:border-white/50 transition-colors"
                  title={name}
                >
                  <SocialIcon size={16} />
                </a>
              ))}
            </div>
            <p className="text-white/40 text-[12px]">
              &copy; {currentYear} Orbiax. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </div>

      <div className="relative z-10 bg-black border-t border-white/10 px-5 sm:px-8 md:px-10 py-4">
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
          {LEGAL_LINKS.map((label) => (
            <button
              key={label}
              type="button"
              className="text-white/40 hover:text-white/70 text-[12px] transition-colors"
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </footer>
  );
};

export default MainframeFooter;
